import React, { useCallback, useState } from 'react';
import { Box, Dialog, Typography } from '@mui/material';
import { AppDialogTitle } from '../../layout/appDialogTitle';
import { AppRow } from '../../layout/appRow';
import { useSelector } from 'react-redux';
import { selectProfile } from '../../store/profileSlice';
import { format } from 'date-fns';
import { ColorPalette } from '../../styles/constants';
import { AppButtonOutlined } from '../app/button/appButtonOutlined';
import { ReportFilters } from '../../models/reports';

import {
  ActivityDetailedReport,
  Report,
  SynthesisReport,
} from '../../models/report';
import { useUserRoles } from '../../hooks/useUserRoles';
import { RolesNames } from '../../models/employee';
import {
   formatDateOnly,
} from '../../services/dateService';

interface ExportDialogProps {
  isOpen: boolean;
  handleClose: () => void;
  filters: ReportFilters;
  handleDownloadActivityExcel: (report: Report) => Promise<void>;
}

const styles = {
  dialog: {
    maxWidth: '530px',
    overflowX: 'hidden',
    position: 'relative',
  },
  topBox: {
    p: 3,
    background: ColorPalette.BACKGROUND,
  },
  inputDataBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    mb: 2,
  },
  inputValue: {
    color: ColorPalette.RAISIN_BLACK,
  },
  btn: {
    width: '200px',
  },
  btnBox: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-evenly',
    rowGap: 2,
  },
};

export function ActivityExport({
  isOpen,
  handleClose,
  filters,
  handleDownloadActivityExcel,
}: ExportDialogProps): JSX.Element {
  const profile = useSelector(selectProfile);
  const { isAdmin } = useUserRoles();
  const [isLoadingSynthesis, setIsLoadingSynthesis] = useState(false);
  const [isloadingActivity, setIsLoadingActivity] = useState(false);

  const { startDate, endDate, employeeName, managerName } =
    filters;

  const synthesisReportDownloadCallback = useCallback(() => {
    setIsLoadingSynthesis(true);
    handleDownloadActivityExcel(
      new SynthesisReport(filters, isAdmin)
    ).finally(() => setIsLoadingSynthesis(false));
  }, [filters, handleDownloadActivityExcel, isAdmin]);

  const activityReportDownloadCallback = useCallback(() => {
    setIsLoadingActivity(true);
    handleDownloadActivityExcel(
      new ActivityDetailedReport(filters, isAdmin)
    ).finally(() => setIsLoadingActivity(false));
  }, [filters, handleDownloadActivityExcel, isAdmin]);

  return (
    <Dialog open={isOpen} PaperProps={{ sx: styles.dialog }}>
      <AppDialogTitle
        title="Export"
        handleClose={handleClose}
        sx={{ position: 'sticky', top: 0 }}
      />
      <Box sx={styles.topBox}>
        <AppRow sx={styles.inputDataBox}>
          <Typography>
            {`${RolesNames.TEAM_LEAD}: `}
            <span style={styles.inputValue}>{`${
              managerName === null && isAdmin
                ? 'ALL'
                : managerName || profile?.name
            }`}</span>
          </Typography>
          <Typography>
            {`Engineer: `}
            <span style={styles.inputValue}>{`${employeeName}`}</span>
          </Typography>
          <Typography>
            {`Project: `}
            <span style={styles.inputValue}>ALL</span>
          </Typography>
          <Typography>
            {`Begin date: `}
            <span style={styles.inputValue}>{`${format(
              new Date(formatDateOnly(startDate)),
              'dd/MM/yyy'
            )}`}</span>
          </Typography>
          <Typography>
            {`End date: `}
            <span style={styles.inputValue}>{`${format(
              new Date(formatDateOnly(endDate)),
              'dd/MM/yyy'
            )}`}</span>
          </Typography>
        </AppRow>
        <Box sx={styles.btnBox}>
          <AppButtonOutlined
            handleClick={synthesisReportDownloadCallback}
            isLoading={isLoadingSynthesis}
            hideIcon
            sx={styles.btn}
            label="Activity synthesis"
          />
          <AppButtonOutlined
            handleClick={activityReportDownloadCallback}
            isLoading={isloadingActivity}
            hideIcon
            sx={styles.btn}
            label="Activity detailed"
          />
        </Box>
      </Box>
    </Dialog>
  );
}
